import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import CurrencyIconSVG from "../../resources/svg/CurrencyIconSVG";
import ClockIconSVG from "../../resources/svg/ClockIconSVG";
import ContactsPeopleIconSVG from "../../resources/svg/ContactsPeopleIconSVG";
import useScreenSize from "../../../custom-hooks/base-hooks/useScreenSize";
import LocationIconSVG from "../../resources/svg/LocationIconSVG";
import SharePopup from "../../resources/components/popups/SharePopup";
import { WorkDTO } from "../../../domain/models/dto/WorkDTO";
import { getNormalizedDate, getNormalizedTime } from "../../../tools/DatetimeTools";
import {
    getWorkCreatedTime,
    getWorkDistance,
    isCurrentWorkOwner,
    selectWorkAvailableToAddApplication,
    selectWorkAvailableToComplete,
    selectWorkAvailableToDelete,
} from "../../../slices/works-slice";
import DeleteWork from "./DeleteWork";
import { useSelector } from "react-redux";
import AddToSavedButton from "../../resources/components/AddToSavedButton";
import CurrencyAmount from "../../resources/components/CurrencyAmount";
import WorkLabels from "../../resources/components/work-components/WorkLabels";
import CompleteWork from "./CompleteWork";
import { selectUserCompletion } from "../../../slices/user-slice";
import CompleteAccountPopup from "./CompleteAccountPopup";
import _ from "lodash";
import { Link } from "react-router-dom";

interface WorkInfoCardProps {
    work: WorkDTO;
}

const WorkInfoCard: FC<WorkInfoCardProps> = (props) => {
    const [isCompleteAccountPopupVisible, setCompleteAccountPopupVisible] = useState(false);
    const { upTo650, moreOrEqual650, upTo800, moreOrEqual800 } = useScreenSize();
    const { t } = useTranslation();
    const isWorkAvailableToDelete = useSelector(selectWorkAvailableToDelete(props.work));
    const isWorkAvailableToComplete = useSelector(selectWorkAvailableToComplete(props.work));
    const isWorkAvailableToAddApplication = useSelector(selectWorkAvailableToAddApplication(props.work));
    const isUserAccountComplete = useSelector(selectUserCompletion());
    const isOwner = useSelector(isCurrentWorkOwner(props.work));
    const addApplicationButtonHandler = () => {
        isUserAccountComplete
            ? window.scrollTo({ top: 600, behavior: "smooth" })
            : setCompleteAccountPopupVisible(true);
    };

    return (
        <div className="relative flex tmax5:flex-col tmin5:justify-between tmin5:gap-[10px] w-full bg-backgroundInput rounded-lg px-12 dmax2:px-4 py-8 dmax2:py-6 mb-[60px] dmax2:mb-[40px]">
            <div className="flex flex-col dmin2:w-[738px] max-w-full">
                <div className="flex start justify-between">
                    <WorkLabels work={props.work} biggerVersion />
                    {upTo800 && (
                        <div className="H16 dmax2:H12 text-textFaded flex flex-col items-end">
                            <p className="tmax4:w-[150px] text-end text-nowrap">{getWorkCreatedTime(t, props.work)}</p>
                            <p className="tmax4:w-[150px] text-end text-nowrap mt-2 dmax2:mt-1">
                                {t("work.viewCount", { viewCount: props.work.viewCount })}
                            </p>
                        </div>
                    )}
                </div>
                <p className="H32 dmax2:H20B text-textWhite max-w-full break-words mt-7 dmax2:mt-5">
                    {props.work.title}
                </p>
                <div className="mt-3">
                    <div className="flex flex-wrap gap-x-2 gap-y-1.5">
                        <span className="H20 dmax2:H16 text-textFaded">{t("work.inCategory")}</span>
                        {props.work.categories.map((category) => (
                            <Link
                                key={`work-category-${category.category}`}
                                id={category.id}
                                to={`/catalog?categories=${category.category}`}
                                className="H20 dmax2:H16B text-textFaded hover:text-textWhite underline decoration-1 underline-offset-[8px] transition duration-300"
                            >
                                {t(`work.categories.${category.category}`)}
                            </Link>
                        ))}
                    </div>
                </div>
                <div className="flex flex-wrap gap-4 dmax2:gap-2 mt-7 dmax2:mt-5">
                    <div className="H16 flex justify-center items-center dmax2:H14 text-textWhite bg-primaryBlue rounded-[30px] w-fit tmax5:h-[30px] px-4 dmax2:px-2.5 py-2 dmax2:py-[7px]">
                        <CurrencyIconSVG className="w-6 dmax2:w-4 h-6 dmax2:h4 mr-3 dmax2:mr-1.5 text-textWhite" />
                        <span>
                            <CurrencyAmount price={props.work.price} suffix={t("work.applicationCreation.priceUnits")} />
                        </span>
                    </div>
                    <div className="H16 flex justify-center items-center dmax2:H14 text-textWhite bg-primaryBlue rounded-[30px] w-fit tmax5:h-[30px] px-4 dmax2:px-2.5 py-2 dmax2:py-[7px]">
                        <ClockIconSVG className="w-6 dmax2:w-4 h-6 dmax2:h4 mr-3 dmax2:mr-1.5 text-textWhite" />
                        <span>
                            {props.work.estimatedHours} {t("work.estimatedHours")}
                        </span>
                    </div>
                    <div className="H16 flex justify-center items-center dmax2:H14 text-textWhite bg-primaryBlue rounded-[30px] w-fit tmax5:h-[30px] px-4 dmax2:px-2.5 py-2 dmax2:py-[7px]">
                        <ContactsPeopleIconSVG className="w-6 dmax2:w-4 h-6 dmax2:h4 mr-3 dmax2:mr-1.5 text-textWhite" />
                        <span>
                            {props.work.workersAmount} {t("work.workersAmount")}
                        </span>
                    </div>
                </div>
                <div className="flex items-center mt-7 dmax2:mt-5">
                    <p className="H20 dmax2:H16 text-textWhite underline decoration-1 underline-offset-[4px]">
                        {props.work.address.country}, {props.work.address.city}, {props.work.address.address}
                    </p>
                    {moreOrEqual650 && (
                        <>
                            <span className="H20 dmax2:H16 text-textWhite text-nowrap ml-2 dmax2:ml-1.5">
                                {getWorkDistance(props.work, t)}
                            </span>
                            <LocationIconSVG className="min-w-6 min-h-6 ml-3 text-textWhite" />
                        </>
                    )}
                </div>
                {upTo650 && (
                    <div className="flex items-center mt-4">
                        <LocationIconSVG className="w-6 h-6 mr-3 text-textWhite" />
                        <span className="H20 dmax2:H16 text-textWhite text-nowrap ml-2 dmax2:ml-1.5">
                            {getWorkDistance(props.work, t)}
                        </span>
                    </div>
                )}
                <div className="mt-4">
                    <span className="H20 dmax2:H16 text-textFaded">{t("work.workStart")}</span>
                    <span className="H20B dmax2:H14B text-textWhite ml-[18px] dmax2:ml-2">
                        {getNormalizedDate(props.work.startFrom)}
                    </span>
                    <span className="H20 dmax2:H14B text-textWhite ml-5 dmax2:ml-3">
                        {getNormalizedTime(props.work.startFrom)}
                    </span>
                </div>
                {!isOwner && (
                    <div className="flex mt-7 dmax2:mt-5 tmax5:justify-between">
                        {isWorkAvailableToAddApplication && (
                            <div
                                onClick={addApplicationButtonHandler}
                                className="button dmin2:H16 td2:H14 tmax1:H12 dmin2:h-[50px] td2:h-[40px] tmax1:h-[37px] px-6 mr-4 transition duration-300"
                            >
                                <div className="mb-[2px]">{t("work.apply")}</div>
                            </div>
                        )}
                        <div className="flex items-center cursor-pointer group">
                            <AddToSavedButton work={props.work} labelEnabled />
                        </div>
                    </div>
                )}
            </div>
            <div className="td3:absolute td3:right-4 flex tmin5:flex-col tmin5:justify-between tmax5:justify-start td3:h-[calc(100%-48px)]">
                {moreOrEqual800 && (
                    <div className="H16 dmax2:H12 text-textFaded flex flex-col tmin5:items-end">
                        <p className="text-end text-nowrap">{getWorkCreatedTime(t, props.work)}</p>
                        <p className="text-end text-nowrap mt-2 dmax2:mt-1">
                            {t("work.viewCount", { viewCount: props.work.viewCount })}
                        </p>
                    </div>
                )}
                <div className="flex flex-col tmin5:items-end tmin5:gap-4 tmax5:gap-6 tmax5:mt-6">
                    <SharePopup />
                    {(isWorkAvailableToDelete || isWorkAvailableToComplete) && (
                        <div className="flex tmin5:flex-col tmin5:items-end gap-3 dmin2:gap-4">
                            {isWorkAvailableToDelete && <DeleteWork workId={props.work.id} />}
                            {isWorkAvailableToComplete && <CompleteWork workId={props.work.id} />}
                        </div>
                    )}
                </div>
            </div>
            <CompleteAccountPopup
                isVisible={isCompleteAccountPopupVisible}
                setIsVisible={setCompleteAccountPopupVisible}
            />
        </div>
    );
};

export default WorkInfoCard;
